import { useSortable } from '@dnd-kit/sortable';
import { Box } from '@mui/material';

import { Draghandle } from '@/components/lists/DragAndDropList/Draghandle';
import { getSortableItemStyle } from '@/components/lists/DragAndDropList/getSortableItemStyle';

export interface SortableRecipientItemProps {
  id: string;
  disabled?: boolean;
  children: (draghandle: React.ReactNode) => React.ReactNode;
}

/**
 * A sortable list item wrapper that provides a drag handle to its children via render prop.
 * Used for recipient lists in Dispositive Provisions forms.
 *
 * @example
 * ```tsx
 * <SortableRecipientItem id={item._id} disabled={isDisabled}>
 *   {(Draghandle) => (
 *     <RecipientCard Draghandle={Draghandle} />
 *   )}
 * </SortableRecipientItem>
 * ```
 */
export function SortableRecipientItem({
  id,
  disabled = false,
  children,
}: SortableRecipientItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled });

  const style = getSortableItemStyle({ transform, transition, isDragging });

  // Always render the drag handle for visual consistency.
  // When disabled, render without interaction props so it's visible but non-functional.
  const draghandle = disabled ? (
    <Draghandle />
  ) : (
    <Draghandle {...listeners} {...attributes} />
  );

  return (
    <Box
      component="li"
      ref={setNodeRef}
      style={style}
      sx={{ listStyleType: 'none', p: 0, m: 0, mb: 2 }}
    >
      {children(draghandle)}
    </Box>
  );
}
